import * as z from "zod";

export const partnerStatusSchema = z.enum(["active", "paused", "terminated"]);
export type PartnerStatus = z.infer<typeof partnerStatusSchema>;

export const partnerTypeSchema = z.enum(["individual", "app", "agency"]);
export type PartnerType = z.infer<typeof partnerTypeSchema>;

export const partnerProductCodeSchema = z.enum(["chargebacks"]);
export type PartnerProductCode = z.infer<typeof partnerProductCodeSchema>;

export const partnerInstallOrderVolumeSchema = z.object({
  recordedAt: z.iso.datetime(),
  last30Days: z.number().int().min(0).optional(),
  lastYear: z.number().int().min(0).optional(),
});
export type PartnerInstallOrderVolume = z.infer<
  typeof partnerInstallOrderVolumeSchema
>;

export const partnerInstallMetricsSnapshotSchema = z.object({
  onlineStoreOrderVolume: partnerInstallOrderVolumeSchema.optional(),
  totalOrderVolume: partnerInstallOrderVolumeSchema.optional(),
});
export type PartnerInstallMetricsSnapshot = z.infer<
  typeof partnerInstallMetricsSnapshotSchema
>;

export const partnerInstallSchema = z.object({
  id: z.string(),
  shopName: z.string(),
  shopDomain: z.string(),
  teamId: z.string().nullable(),
  metricsSnapshot: partnerInstallMetricsSnapshotSchema.nullable(),
  productCode: partnerProductCodeSchema,
  installedAt: z.iso.datetime(),
});
export type PartnerInstall = z.infer<typeof partnerInstallSchema>;

/**
 * Shape returned by list-partners and used as the row type in tables.
 */
export const partnerSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  referralCode: z.string(),
  profileImageUrl: z.string().url().nullable(),
  partnerType: partnerTypeSchema,
  status: partnerStatusSchema,
  installCount: z.number().int().min(0),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PartnerSummary = z.infer<typeof partnerSummarySchema>;

/**
 * Auth-event row included in partner detail views. Free-form `eventType`
 * because new types may be added (admin-side audits, social-login, etc.)
 * without forcing a schema bump on every consumer.
 */
export const partnerAuthEventSchema = z.object({
  id: z.string(),
  partnerId: z.string().nullable(),
  email: z.string().nullable(),
  eventType: z.string(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  magicLinkJti: z.string().nullable(),
  occurredAt: z.string(),
});
export type PartnerAuthEvent = z.infer<typeof partnerAuthEventSchema>;

/**
 * Shape returned by get-partner. Includes the most-recent auth events
 * so the detail page can render them inline without a follow-up RPC.
 */
export const partnerDetailSchema = partnerSummarySchema.extend({
  recentAuthEvents: z.array(partnerAuthEventSchema),
});
export type PartnerDetail = z.infer<typeof partnerDetailSchema>;
